import { useIsMobile } from '@yes.technology/react-toolkit'
import { useSearchParams } from 'react-router-dom'
import { FilterableMapProps } from './FilterableMap.types'

import { SafeHtmlContent } from 'site/shared/components/SafeHtmlContent'
import brazilMapShapes from './assets/BrazilMapShapeUrl'
import FilteredObjects from './components/FilteredObjects/FilteredObjects'
import { Container } from './components/FilteredObjects/styles'
import MapContentTabs from './components/MapContentTabs/MapContentTabs'
import SelectableMap from './components/SelectableMap/SelectableMap'
import useFilterableMapMethods from './hooks/useFilterableMapMethods/useFilterableMapMethods'
import useMapFilteredObjects from './hooks/useFilterableMapQuery/useFilterableMapQuery'
import useMapTabs from './hooks/useFilterableMapTabs/useFilterableMapTabs'
import { ContentContainer, StyledText } from './styles'

const FilterableMap = ({ props }: FilterableMapProps) => {
  const {
    filteractionUuid,
    objectclassUuid,
    selectedStateColor = '#3E6F6B',
    defaultStateColor = '#F1F1F1',
    strokeColor = '#cfcfcf',
    selectedStateStrokeColor = '#ffffff',
    mobileDefaultStrokeColor = '#666666',
    mobileSelectedStateStrokeColor = '#ffffff',
    actionTarget = '_blank',
    buttonProps,
    referencePeriodText,
    initialSelectedState = {
      brasil: 'BR-SP',
      chile: 'CL-ST'
    },
    availableTabs = {
      brasil: ['federal', 'state'],
      chile: ['federal']
    },
    mapsMaxWidth = {
      brasil: '840px',
      chile: '200px'
    },
    mapDescription = {}
  } = props

  const [searchParams] = useSearchParams()
  const country = searchParams.get('country') ?? 'brasil'

  const { selectedTab, setSelectedTab, tabsItems } = useMapTabs({
    country,
    availableTabs
  })

  const { isLoading, items, updateQuery } = useMapFilteredObjects({
    country,
    filteractionUuid,
    objectclassUuid
  })

  const {
    mapContainerRef,
    tabsRef,
    filteredObjectsRef,
    selectedState,
    onStateChange,
    onTabChange
  } = useFilterableMapMethods({
    country,
    initialSelectedState,
    setSelectedTab,
    updateQuery
  })

  const isMobile = useIsMobile()

  const brasilMapShapeUrl = brazilMapShapes[isMobile ? 'mobile' : 'desktop']

  const mode = selectedTab?.uuid

  const description = mapDescription[country] ?? ''

  return (
    <>
      <ContentContainer ref={mapContainerRef}>
        {description && (
          <StyledText>{<SafeHtmlContent html={description} />}</StyledText>
        )}
        <SelectableMap
          selectedState={selectedState}
          onChange={onStateChange}
          disableMapInteractions={mode === 'federal'}
          displayLabels={mode === 'state'}
          selectedStateColor={selectedStateColor}
          defaultStateColor={defaultStateColor}
          strokeColor={strokeColor}
          selectedStateStrokeColor={selectedStateStrokeColor}
          mobileDefaultStrokeColor={mobileDefaultStrokeColor}
          mobileSelectedStateStrokeColor={mobileSelectedStateStrokeColor}
          referencePeriodText={referencePeriodText}
          displayZoomControls={mode === 'state'}
          country={country}
          key={country}
          mapsMaxWidth={mapsMaxWidth}
        />
      </ContentContainer>
      <Container
        style={{
          backgroundImage: brasilMapShapeUrl
        }}
        ref={filteredObjectsRef}
      >
        <MapContentTabs
          tabs={tabsItems[country]}
          selectedItem={selectedTab}
          onItemSelect={onTabChange}
          highlightColor={selectedStateColor}
          ref={tabsRef}
        />
        <FilteredObjects
          objects={items}
          actionColor={selectedStateColor}
          target={actionTarget}
          buttonProps={buttonProps}
          isLoading={isLoading}
        />
      </Container>
    </>
  )
}

export default FilterableMap
